import styled from 'styled-components';
import { MicrostoreLocationTemplateFormValues } from './FormHelper/DataMapper';
import { LocationBadges } from './LocationBadges';
import {
    getLocationBadges,
    getLocationBackgroundColor,
    getLocationBorderColor,
    getLocationDisplayName,
    getLocationProductCount,
    getLocationStatus,
} from './utils/locationBadgeHelpers';
import { computeColorFromName } from 'helpers/computeColorFromName';
import { ProductState } from 'data/__generated__';

type LocationCellProps = {
    location: MicrostoreLocationTemplateFormValues;
    isSelected: boolean;
    onClick: () => void;
    shelfIndex: number;
    rowIndex: number;
    columnIndex: number;
};

export const LocationCell = ({
    location,
    isSelected,
    onClick,
    shelfIndex,
    rowIndex,
    columnIndex,
}: LocationCellProps) => {
    const status = getLocationStatus(location);
    const badges = getLocationBadges(location);
    const displayName = getLocationDisplayName(location);
    const productCount = getLocationProductCount(location);
    const backgroundColor = getLocationBackgroundColor(status);
    const borderColor = getLocationBorderColor(status, isSelected);

    const { productArrangement, product } = location;

    // Double coil if the product is double coil OR if at least one product of the arrangement is double coil
    const isDoubleCoil =
        product?.isDoubleCoil ||
        productArrangement?.productsWithStockMax?.some((p) => p.product?.isDoubleCoil) ||
        false;

    // Stock max info
    const hasStockInfo = location.stockMax !== null;
    const stockMaxDisplay = hasStockInfo && status !== 'empty' ? `${location.stockMax ?? '-'}` : null;

    return (
        <CellContainer
            onClick={onClick}
            $backgroundColor={backgroundColor}
            $borderColor={borderColor}
            $isSelected={isSelected}
            $isEmpty={status === 'empty'}
            $isDoubleCoil={isDoubleCoil}
            title={`Étagère ${shelfIndex + 1} - Rangée ${rowIndex + 1} - Colonne ${columnIndex + 1}`}
        >
            {status === 'empty' ? (
                <EmptyIndicator>⚪</EmptyIndicator>
            ) : (
                <>
                    {/* Stock max en haut à droite */}
                    {stockMaxDisplay && <StockMaxBadge>{stockMaxDisplay}</StockMaxBadge>}

                    {/* Image ou couleur pour le produit/arrangement */}
                    {productArrangement && (
                        <ProductArrangementColor color={computeColorFromName(productArrangement.name)} />
                    )}

                    {product && product.imageUrl && <ProductImage src={product.imageUrl} alt={product.name} />}

                    {product && !product.imageUrl && (
                        <ProductArrangementColor color={computeColorFromName(product.name)} />
                    )}

                    {/* Nom du produit/arrangement */}
                    <ProductName>
                        {isDoubleCoil ? `[DOUBLE SPIRE] - ${displayName}` : displayName}
                        {product && product.state !== ProductState.Regular && (
                            <ErrorMessage>État: {product.state}</ErrorMessage>
                        )}
                        {product && !product.isMicrostore && <ErrorMessage>Non dispo microstore</ErrorMessage>}
                        {product && !product.imageUrl && <ErrorMessage>Sans image</ErrorMessage>}
                        {product && product.isToBeArchived && <ErrorMessage>À sortir</ErrorMessage>}
                    </ProductName>

                    {/* Nombre de produits */}
                    <ProductCountText>{productCount}</ProductCountText>

                    {/* Badges en bas à droite */}
                    <BadgesWrapper>
                        <LocationBadges badges={badges} />
                    </BadgesWrapper>
                </>
            )}
        </CellContainer>
    );
};

const CellContainer = styled.div<{
    $backgroundColor: string;
    $borderColor: string;
    $isSelected: boolean;
    $isEmpty: boolean;
    $isDoubleCoil: boolean;
}>`
    position: relative;
    cursor: pointer;
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 1 calc(200px + 6px)' : '1 1 100px')};
    min-height: 120px;
    border: 2px solid ${({ $borderColor }) => $borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 8px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    display: flex;
    flex-direction: column;
    justify-content: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'space-between')};
    align-items: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'stretch')};
    box-shadow: ${({ $isSelected, theme }) => ($isSelected ? theme.boxShadow : 'none')};
    transition: all 0.2s ease;

    &:hover {
        transform: ${({ $isEmpty }) => ($isEmpty ? 'none' : 'scale(1.02)')};
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
`;

const EmptyIndicator = styled.div`
    font-size: 32px;
    opacity: 0.3;
`;

const ProductArrangementColor = styled.div`
    align-self: flex-start;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ color }) => color};
`;

const ProductImage = styled.img`
    width: 60px;
    height: 50px;
    object-fit: cover;
    object-position: center;
    align-self: center;
    border-radius: 4px;
`;

const ProductName = styled.div`
    text-align: center;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    padding: 4px 0;
    word-break: break-word;
`;

const ErrorMessage = styled.div`
    color: #d31500;
    font-size: 11px;
    margin-top: 2px;
    font-weight: 400;
`;

const ProductCountText = styled.div`
    align-self: flex-start;
    font-size: 11px;
    color: #666;
    margin-bottom: 4px;
`;

const BadgesWrapper = styled.div`
    position: absolute;
    bottom: 6px;
    right: 6px;
    display: flex;
    gap: 2px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    padding: 4px 6px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StockMaxBadge = styled.div`
    position: absolute;
    top: 6px;
    right: 6px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #333;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
`;
